import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@volca/ui/components/ui/collapsible";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { GOOGLE_BUSINESS_CONNECTION_STATE } from "@/business/constants";
import { useGetGoogleBusinessAccountLocations } from "@/business/google-business-query";
import { useGetServiceTitanCredentials } from "@/business/service-titan-query";
import useBusiness from "@/core/business/use-business";
import { useGetAccountSummary } from "@/internal-transactions/query";
import { useGetTechnicians } from "@/user/query";
import { GoogleBusinessPostOnboardingModal } from "./google-business-post-onboarding-modal";
import { OnboardingActionButton } from "./onboarding-action-button";
import PaymentModal from "./payment-modal";
import { ServiceTitanIntegrationModal } from "./service-titan-integration-modal";
import { TechnicianUploadModal } from "./technician-upload-modal";

interface PostOnboardingItemsProps {
  className?: string;
  collapsible?: boolean;
}

const PostOnboardingItems = ({
  className,
  collapsible = false,
}: PostOnboardingItemsProps) => {
  const { selectedBusiness } = useBusiness();
  const { id: businessId, dots_complete } = selectedBusiness || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [technicianModalOpen, setTechnicianModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [serviceTitanModalOpen, setServiceTitanModalOpen] = useState(false);
  const [googleBusinessModalOpen, setGoogleBusinessModalOpen] = useState(false);

  const { data: technicians = [] } = useGetTechnicians(businessId);
  const { data: accountSummary } = useGetAccountSummary(businessId);
  const { data: serviceTitanCredentials } =
    useGetServiceTitanCredentials(businessId);
  const { data: googleBusinessLocations = [] } =
    useGetGoogleBusinessAccountLocations(businessId);

  const hasTechnicians = technicians.length > 0;
  const hasFunds = accountSummary && Number(accountSummary.wallet_balance) > 0;
  const hasServiceTitanIntegration =
    serviceTitanCredentials?.credentials_status === "connection_established";
  const hasGoogleBusinessConnection =
    selectedBusiness?.google_business_connection_state ===
      GOOGLE_BUSINESS_CONNECTION_STATE.connected &&
    googleBusinessLocations.length > 0;

  const handleTechnicianClick = () => {
    if (!hasTechnicians) {
      setTechnicianModalOpen(true);
    }
  };

  const handlePaymentClick = () => {
    if (!hasFunds) {
      setPaymentModalOpen(true);
    }
  };

  const handleServiceTitanClick = () => {
    if (!hasServiceTitanIntegration) {
      setServiceTitanModalOpen(true);
    }
  };

  const handleGoogleBusinessClick = () => {
    if (!hasGoogleBusinessConnection) {
      setGoogleBusinessModalOpen(true);
    }
  };

  const handleTechnicianComplete = () => {
    setTechnicianModalOpen(false);
  };

  const handlePaymentComplete = () => {
    setPaymentModalOpen(false);
  };

  useEffect(() => {
    const googleBusinessModal = searchParams.get("google_business_modal");
    if (googleBusinessModal === "open") {
      setGoogleBusinessModalOpen(true);
      searchParams.delete("google_business_modal");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const checklist = (
    <Card className={cn("w-full max-w-md gap-0 p-6", className)}>
      <Stack className="w-full" align="start" gap={4}>
        <Stack className="w-full" align="start" gap={1}>
          <Text variant="title" render={<h2 />}>
            Finish setting up Volca
          </Text>
          <Text tone="muted">These connect Volca to your team and tools.</Text>
        </Stack>

        <Stack className="w-full" align="start" gap={0}>
          <OnboardingActionButton
            text="Upload a list of your technicians"
            isCompleted={hasTechnicians}
            onClick={handleTechnicianClick}
          />
          {dots_complete && (
            <OnboardingActionButton
              text="Deposit your first funds"
              isCompleted={hasFunds}
              onClick={handlePaymentClick}
            />
          )}
          <OnboardingActionButton
            text="Set up ServiceTitan integration"
            isCompleted={hasServiceTitanIntegration}
            onClick={handleServiceTitanClick}
          />
          <OnboardingActionButton
            text="Connect your Google Business account"
            isCompleted={hasGoogleBusinessConnection}
            onClick={handleGoogleBusinessClick}
          />
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <>
      {collapsible ? (
        <Collapsible className="w-full max-w-md">
          <CollapsibleTrigger
            render={
              <Button variant="ghost" size="sm" className="group text-subtle" />
            }
          >
            Setup checklist
            <CaretDownIcon
              data-icon="inline-end"
              className="transition-transform group-aria-expanded:rotate-180 motion-reduce:transition-none"
            />
          </CollapsibleTrigger>
          <CollapsibleContent keepMounted className="pt-3">
            {checklist}
          </CollapsibleContent>
        </Collapsible>
      ) : (
        checklist
      )}
      <TechnicianUploadModal
        isOpen={technicianModalOpen}
        onOpenChange={setTechnicianModalOpen}
        onComplete={handleTechnicianComplete}
        businessId={businessId}
      />

      <PaymentModal
        isOpen={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        onComplete={handlePaymentComplete}
        businessId={businessId}
      />

      <ServiceTitanIntegrationModal
        isOpen={serviceTitanModalOpen}
        onOpenChange={setServiceTitanModalOpen}
        businessId={businessId}
      />

      <GoogleBusinessPostOnboardingModal
        isOpen={googleBusinessModalOpen}
        onOpenChange={setGoogleBusinessModalOpen}
        businessId={businessId}
        business={selectedBusiness}
      />
    </>
  );
};

export default PostOnboardingItems;
